import React, { useEffect, useState } from 'react';
import { Fingerprint, Building, Info, ShieldCheck, ExternalLink } from 'lucide-react';
import { api } from '../api';

function ConfBadge({ c }) {
  const style = ({
    high:   { bg: '#E5F0EA', fg: '#2D5F4F' },
    medium: { bg: '#FBE6CC', fg: '#A06628' },
    low:    { bg: '#F0E7D2', fg: '#5A6660' },
  })[c] || { bg: '#F0E7D2', fg: '#5A6660' };
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
          style={{ background: style.bg, color: style.fg }}>{c}</span>
  );
}

function Row({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[#F0E7D2] last:border-b-0">
      <span className="text-[11px] uppercase tracking-wider text-[#7A857F]">{label}</span>
      <span className={`text-[12.5px] text-[#1A2622] text-right ${mono ? 'font-mono tracking-tight' : ''}`}>{value}</span>
    </div>
  );
}

/**
 * Compact "Identifier & Enrollment Information" panel.
 * Renders for facility, hospital, or physician. Pulls fallback values from `fallback`
 * (the existing entity doc) so the panel is never empty even before NPPES match exists.
 */
export default function ProviderIdentifierPanel({ entityType, entityId, fallback = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityType || !entityId) return;
    setLoading(true);
    api.identifier(entityType, entityId)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entityType, entityId]);

  if (loading) return null;
  const enriched = data && data.match_status === 'matched';
  const npi = data?.npi;
  const idLabel = entityType === 'physician' ? 'Individual NPI' :
                  entityType === 'hospital' ? 'Hospital CCN' : 'Facility CCN';
  const ccn = fallback.ccn || (entityType === 'physician' ? null : entityId);

  return (
    <section className="rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="identifier-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Fingerprint size={14} className="text-[#34538F]" />
        <h3 className="font-display text-base font-semibold text-[#1A2622]">Identifier &amp; Enrollment Information</h3>
        {enriched
          ? <ConfBadge c={data.match_confidence} />
          : <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F0E7D2] text-[#5A6660]">awaiting NPPES sync</span>}
        <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E5F0EA] text-[#2D5F4F]">
          <ShieldCheck size={10} /> public CMS data
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <Row label={idLabel} value={ccn || fallback.npi || entityId} mono />
          {npi && entityType !== 'physician' && <Row label="Organization NPI" value={npi} mono />}
          {entityType === 'physician' && <Row label="Individual NPI" value={npi || entityId} mono />}
          {data?.primary_taxonomy_code && (
            <Row label="Primary Taxonomy" value={
              <span>{data.primary_taxonomy_desc}
                <span className="text-[10px] text-[#7A857F] font-mono ml-1.5">{data.primary_taxonomy_code}</span>
              </span>
            } />
          )}
          {data?.status && <Row label="NPPES status" value={
            <span className={data.status === 'A' ? 'text-[#2D5F4F] font-semibold' : 'text-[#A06628] font-semibold'}>
              {data.status === 'A' ? 'Active' : data.status}
            </span>
          } />}
          {data?.enumeration_date && <Row label="Enumeration date" value={new Date(data.enumeration_date).toLocaleDateString()} />}
        </div>

        <div>
          {data?.legal_business_name && <Row label="Legal Business Name" value={data.legal_business_name} />}
          {!data?.legal_business_name && fallback.legal_name && <Row label="Legal Business Name" value={fallback.legal_name} />}
          {data?.dba_names?.length > 0 && (
            <Row label="DBA / Other Names" value={
              <div className="text-right">
                {data.dba_names.slice(0, 3).map((n, i) => <div key={i}>{n}</div>)}
              </div>
            } />
          )}
          {(fallback.ownership_type || fallback.ownership) && (
            <Row label="Ownership type" value={fallback.ownership_type || fallback.ownership} />
          )}
          {fallback.chain_name && (
            <Row label="Parent / Chain" value={fallback.chain_name} />
          )}
          {data?.authorized_official && (
            <Row label="Authorized Official" value={
              <span>{data.authorized_official}
                {data.authorized_official_title && <span className="text-[10px] text-[#7A857F] block">{data.authorized_official_title}</span>}
              </span>
            } />
          )}
        </div>
      </div>

      {data?.addresses?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F0E7D2]">
          <div className="text-[11px] uppercase tracking-wider text-[#7A857F] mb-1.5">Practice addresses</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.addresses.slice(0, 4).map((a, i) => (
              <div key={i} className="text-[11.5px] text-[#3A4A44] bg-[#FAF5E6] rounded p-2">
                <div className="text-[9.5px] uppercase font-semibold text-[#7A857F]">{a.purpose}</div>
                <div>{a.address_1}</div>
                <div>{a.city}, {a.state} {a.postal_code}</div>
                {a.telephone && <div className="text-[#7A857F] text-[10.5px] mt-0.5">{a.telephone}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {npi && (
        <div className="mt-3 pt-3 border-t border-[#F0E7D2] flex items-center gap-2">
          <a
            href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-[#34538F] hover:underline"
            data-testid="identifier-nppes-link"
          >
            View on NPPES <ExternalLink size={10} />
          </a>
        </div>
      )}

      <div className="mt-3 flex items-start gap-1.5 text-[10.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={10} className="mt-0.5 shrink-0" />
        <span>
          <strong>Source:</strong> {data?.source || 'NPPES NPI Registry'}.
          {data?.last_updated_nppes && <> NPPES last updated {new Date(data.last_updated_nppes).toLocaleDateString()}.</>}
          {' '}Public-data only · no PHI · no patient-level data.
        </span>
      </div>
    </section>
  );
}

export { Building };  // re-export for callers that want building icon
